import React from 'react';
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useTeamContext } from "../TeamContext";
import "../styles.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const EquityVisualization = () => {
    const { activeWorkspaceId, getWorkspaceById, calculateFinalEquity } = useTeamContext();
    const ws = getWorkspaceById(activeWorkspaceId);

    const {
        finalEquityMap,
        coreBreakdown,
        reservedEquityTotal,
    } = calculateFinalEquity();

    const founderNames = ws?.part1Answers?.founderNames || [];
    const reservedPools = ws?.part1Answers?.reservedPools || [];
    const founderEntries = Object.entries(finalEquityMap || {});

    if (!ws?.part1Answers) return null;

    // Reuse chart functions from report page
    const createChartData = (founderEntries, reservedPools) => ({
        labels: [
            ...founderEntries.map(([fn]) => fn),
            ...reservedPools.map(rp => rp.name)
        ],
        datasets: [{
            data: [
                ...founderEntries.map(([_, eq]) => eq),
                ...reservedPools.map(rp => rp.weight)
            ],
            backgroundColor: [
                "#3b82f6", "#ec4899", "#f97316", "#14B8A6", "#8b5cf6",
                "#f59e0b", "#F43F5E", "#6EE7B7", "#C084FC", "#22D3EE"
            ]
        }]
    });

    const createProjectChartData = () => {
        const breakdown = {};

        let totalFinancial = 0;
        let totalTime = 0;
        let totalAssets = 0;
        founderNames.forEach((fn) => {
            const core = coreBreakdown[fn] || {};
            totalFinancial += core.financial || 0;
            totalTime += core.time || 0;
            totalAssets += core.assets || 0;
        });

        breakdown["Financial Investment"] = totalFinancial;
        breakdown["Time Commitment"] = totalTime;
        breakdown["Asset Contribution"] = totalAssets;

        reservedPools.forEach((rp) => {
            breakdown[rp.name] = rp.weight || 0;
        });

        return {
            labels: Object.keys(breakdown),
            datasets: [{
                data: Object.values(breakdown),
                backgroundColor: [
                    "#EC4899", "#F97316", "#10B8A6", "#3B82F6", "#8b5cf6",
                    "#f59e0b", "#F43F5E", "#6EE7B7", "#C084FC", "#22D3EE"
                ]
            }]
        };
    };

    return (
        <div style={{
            padding: '24px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            margin: '20px 0',
            maxWidth: '1200px',
        }}>
            <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                textAlign: 'center',
                marginBottom: '32px',
                color: '#28435a'
            }}>
                Preliminary Equity Breakdown
            </h3>

            <div className="section">
                <div style={{display: 'flex', gap: '24px', marginBottom: '32px'}}>
                    {/* Project Breakdown Chart */}
                    <div style={{flex: 1, minWidth: '300px'}}>
                        <h4>Breakdown by Project</h4>
                        <div style={{height: '300px'}}>
                            <Pie
                                data={createProjectChartData()}
                                options={{maintainAspectRatio: false}}
                            />
                        </div>
                    </div>

                    {/* Team Breakdown Chart */}
                    <div style={{flex: 1, minWidth: '300px'}}>
                        <h4>Breakdown by Team</h4>
                        <div style={{height: '300px'}}>
                            <Pie
                                data={createChartData(founderEntries, reservedPools)}
                                options={{maintainAspectRatio: false}}
                            />
                        </div>
                    </div>
                </div>

                {/* Core Contributions Table */}
                <h4>Team Member Contributions</h4>
                <table className="equity-summary" style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                }}>
                    <thead>
                    <tr style={{
                        borderBottom: '2px solid #e2e8f0',
                        backgroundColor: '#f8fafc'
                    }}>
                        <th style={{textAlign: 'left', padding: '12px', fontWeight: 600}}>Founder</th>
                        <th style={{textAlign: 'right', padding: '12px', fontWeight: 600}}>Financial</th>
                        <th style={{textAlign: 'right', padding: '12px', fontWeight: 600}}>Time</th>
                        <th style={{textAlign: 'right', padding: '12px', fontWeight: 600}}>Assets</th>
                        <th style={{textAlign: 'right', padding: '12px', fontWeight: 600}}>Total</th>
                    </tr>
                    </thead>
                    <tbody>
                    {founderNames.map(founder => {
                        const financial = coreBreakdown[founder]?.financial || 0;
                        const time = coreBreakdown[founder]?.time || 0;
                        const assets = coreBreakdown[founder]?.assets || 0;

                        return (
                            <tr
                                key={founder}
                                style={{
                                    borderBottom: '1px solid #f1f5f9',
                                    ':nth-child(even)': {backgroundColor: '#f8fafc'}
                                }}
                            >
                                <td style={{padding: '12px', fontWeight: 500}}>{founder}</td>
                                <td style={{textAlign: 'right', padding: '12px'}}>{financial.toFixed(2)}%</td>
                                <td style={{textAlign: 'right', padding: '12px'}}>{time.toFixed(2)}%</td>
                                <td style={{textAlign: 'right', padding: '12px'}}>{assets.toFixed(2)}%</td>
                                <td style={{textAlign: 'right', padding: '12px', fontWeight: 600}}>
                                    {(financial + time + assets).toFixed(2)}%
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>

                {/* Reserved Pools Table */}
                <div style={{
                    backgroundColor: '#f8fafc',
                    padding: '16px',
                    borderRadius: '6px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                    <h4 style={{marginBottom: '16px'}}>Reserved Equity Pools</h4>
                    <table className="equity-summary" style={{width: '100%'}}>
                        <tbody>
                        {reservedPools.map((pool, i) => (
                            <tr key={i}>
                                <td style={{padding: '8px 0'}}>{pool.name}</td>
                                <td style={{textAlign: 'right', padding: '8px 0'}}>
                                    {pool.weight.toFixed(2)}%
                                </td>
                            </tr>
                        ))}
                        <tr style={{
                            borderTop: '1px solid #e2e8f0',
                            fontWeight: 600
                        }}>
                            <td style={{padding: '8px 0'}}>Total Reserved</td>
                            <td style={{textAlign: 'right', padding: '8px 0'}}>
                                {reservedEquityTotal.toFixed(2)}%
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                {/* Total Summary */}
                <div style={{
                    marginTop: '24px',
                    padding: '16px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontWeight: 600,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                    Total Allocated Equity: {(
                    founderNames.reduce((sum, founder) => sum +
                        (coreBreakdown[founder]?.financial || 0) +
                        (coreBreakdown[founder]?.time || 0) +
                        (coreBreakdown[founder]?.assets || 0), 0) +
                    reservedEquityTotal
                ).toFixed(2)}%
                </div>
            </div>
            <div style={{
                marginTop: '32px',
                color: '#28435a',
                borderTop: '1px solid #e5e7eb'
            }}>
                <p style={{lineHeight: 1.6, marginBottom: '12px'}}>
                    The subtotal shown represents your initial equity distribution, calculated from three core factors:
                </p>
                <ul style={{marginBottom: '12px', lineHeight: 1.6}}>
                    <li>Financial Investment</li>
                    <li>Time Investment</li>
                    <li>Asset Investment</li>
                </ul>
                <p style={{lineHeight: 1.6, marginBottom: '12px'}}>
                    Plus the Reserve Equity Pool set aside for future shareholders.
                </p>
                <p style={{lineHeight: 1.6}}>
                    <strong>Important:</strong> However, this is just the foundation. On the next pages, you'll have the
                    opportunity to consider
                    additional contributions that may be so valuable to your venture that they warrant additional equity
                    considerations. While these factors are optional, including them can lead to a more equitable share
                    allocation. The calculator will automatically adjust the equity split, incorporating any additional
                    factors you choose to include.
                </p>
            </div>
        </div>
    );
};

export default EquityVisualization;
